import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, filter, map, switchMap } from 'rxjs';
import { ReferenceRoomSettingsHelper } from '../reference-room-settings.helper';
import { InstallationFeaturesStore, isReady } from '@store';
import {
  DeviceIds,
  Feature,
  FeatureNames,
  RoomChannelsEntityCommands,
  RoomChannelsEntityProperties,
  RoomChannelsProperties,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client/index';

@Injectable({
  providedIn: 'root',
})
export class ReferenceRoomSettingsService {
  private _view$: BehaviorSubject<
    ReferenceRoomSettingsHelper.ReferenceRoomSettingsView
  > = new BehaviorSubject(null);
  private _hcIndex$: BehaviorSubject<string> = new BehaviorSubject(null);

  view$ = this._view$.asObservable();
  hcIndex$ = this._hcIndex$.asObservable();

  channelsData$: Observable<
    ReferenceRoomSettingsHelper.RoomsChannelsEntityData[]
  > = this.installationFeaturesStore
    .selectInstallationDeviceFeature<RoomChannelsProperties, never>(
      FeatureNames.roomsChannels,
      DeviceIds.RoomControl
    )
    .pipe(
      filter((feature) => feature && isReady(feature)),
      switchMap((feature) => {
        const roomsChannelsEntities = feature.properties.enabled.value.map((index) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<
              RoomChannelsEntityProperties,
              RoomChannelsEntityCommands
            >(FeatureNames.roomsChannelsEntity(index), DeviceIds.RoomControl)
            .pipe(
              map(
                (feature): ReferenceRoomSettingsHelper.RoomsChannelsEntityData => ({
                  feature,
                  featureIndex: index,
                  channelId: feature.properties.id.value,
                })
              )
            )
        );

        return combineLatest(roomsChannelsEntities);
      })
    );

  roomsAssignedToChannel$ = combineLatest([
    this.installationFeaturesStore.rooms$,
    this.getRoomsChannelsEntityDataForHeatingCircuit(),
  ]).pipe(
    map(([rooms, { channelId }]) =>
      rooms.filter((room) => room.properties.channels.value.includes(channelId))
    )
  );

  constructor(private installationFeaturesStore: InstallationFeaturesStore) {}

  setView(view: ReferenceRoomSettingsHelper.ReferenceRoomSettingsView): void {
    this._view$.next(view);
  }

  setHcIndex(index): void {
    this._hcIndex$.next(index);
  }

  getRoomsChannelsEntityDataForHeatingCircuit(
    heatingCircuitIndex?: string
  ): Observable<ReferenceRoomSettingsHelper.RoomsChannelsEntityData> {
    if (heatingCircuitIndex) {
      return this.channelsData$.pipe(
        map((data) =>
          data.find((channel) => channel.feature.properties.id.value === heatingCircuitIndex)
        )
      );
    } else {
      return this._hcIndex$.pipe(
        switchMap((hcIndex) =>
          this.channelsData$.pipe(
            map((data) => data.find((channel) => channel.feature.properties.id.value === hcIndex))
          )
        )
      );
    }
  }

  getRoomDetailsForHeatingCircuit(
    heatingCircuitIndex: string
  ): Observable<Feature<RoomProperties, RoomCommands>> {
    return this.getRoomsChannelsEntityDataForHeatingCircuit(heatingCircuitIndex).pipe(
      switchMap((data) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature<
          RoomProperties,
          RoomCommands
        >(FeatureNames.roomDetails(data.featureIndex), DeviceIds.RoomControl)
      )
    );
  }
}
